import { useContext, useMemo } from 'react'
import type { RateView } from 'components/OfferPage/Property/singlePropertyUtils'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { min } from 'lib/array/arrayUtils'
import {
  getPropertyCombinedRatesByRoom,
} from 'selectors/offerPage/offerPageSelectors'

type Return = [
  pricing: App.PricingWithValue | undefined,
  memberPricing: App.PricingWithValue | undefined,
  pricingWithFlights: App.PricingWithValue | undefined,
  memberPricingWithFlights: App.PricingWithValue | undefined,
  rateView: RateView | undefined,
]

// Unfortunately, `EmptyArray` isn't compatible with `Return`.
const emptyReturn: Return = [undefined, undefined, undefined, undefined, undefined]

/**
 * @deprecated This hook (for SPP offers) is being refactored out into selectors. If you _need_ to
 * use this hook, speak to Xiao or Robert.
 *
 * Unlike `<PropertyRoomOption/>`, this hook does _not_ account for GHA-highlighted rates.
 */
function useCheapestPropertyPricing(): Return {
  const offerPageState = useContext(OfferPageStateContext)
  const combinedRatesByRoom = useAppSelector(state =>
    getPropertyCombinedRatesByRoom(offerPageState, state))

  // Combine
  const cheapestRoomRates = useMemo(() => combinedRatesByRoom.flatMap(({ displayRates }) => {
    // We're ignoring the implicit sorting of `displayRates` in case the first element is the
    // GHA-highlighted rate.
    return min(displayRates, ({ price }) => price) ?? []
  }), [combinedRatesByRoom])
  return useMemo(() => {
    const cheapestRate = min(cheapestRoomRates, ({ price }) => price)
    // TODO: should never be property
    if (!cheapestRate || cheapestRate.type === 'property') return emptyReturn

    const {
      pricing,
      memberPricing,
      pricingWithFlights,
      memberPricingWithFlights,
    } = cheapestRate.lpcRate
    return [pricing, memberPricing, pricingWithFlights, memberPricingWithFlights, cheapestRate]
  }, [cheapestRoomRates])
}

export default useCheapestPropertyPricing
